import { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="container-main py-20 text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章不存在</h2>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除。</p>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '欧美一区二区·高清内容聚合平台',
    },
    url: `https://oumei-yiqierqu.com/article/${article.id}`,
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://oumei-yiqierqu.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://oumei-yiqierqu.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const fullContent = article.content || generateContent(article);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      <Navbar />
      <main className="pt-20">
        <article>
          <div className="bg-background-100">
            <div className="container-main py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.categorySlug}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="container-main py-8 md:py-12">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 rounded-md bg-background-200 text-foreground-600 text-xs">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime} 分钟阅读
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500"></i>
                    {article.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views.toLocaleString()} 次阅读
                  </span>
                </div>
              </div>

              <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-10 bg-background-200">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 欧美一区二区`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="prose prose-sm md:prose-base max-w-none">
                <div className="text-foreground-800 leading-relaxed text-sm md:text-base space-y-5">
                  {fullContent}
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500 font-medium">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-8 p-5 rounded-xl bg-background-100 border border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground-900">觉得这篇内容有帮助？</p>
                  <p className="text-xs text-foreground-500 mt-1">分享给同样热爱欧美影视的朋友吧</p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                    <i className="ri-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <section className="bg-background-100 py-14 md:py-20">
              <div className="container-main">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {relatedArticles.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}

function generateContent(article: typeof articles[0]) {
  return (
    <>
      <p>
        {article.excerpt}
      </p>
      <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-4" id="section-overview">内容概览</h2>
      <p>
        在欧美影视的广阔天地中，每一部作品都承载着独特的艺术表达与文化内涵。本文从多个维度出发，为读者呈现全面而深入的内容解析。无论您是资深影迷还是刚接触欧美影视的新观众，都能从中获得有价值的观影指引。
      </p>
      <p>
        欧美一区二区平台始终致力于为用户提供最清晰、最精准的影视分区导航服务。我们深知，在海量内容中找到心仪作品的难度，因此通过科学的分区体系和详尽的文章解读，帮助每一位用户节省时间、提升观影体验。
      </p>
      <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-4" id="section-analysis">深度分析</h2>
      <p>
        从叙事结构来看，优秀的欧美影视作品往往在传统框架中融入创新元素。导演通过精心设计的镜头语言、节奏把控和情感铺陈，将观众带入一个引人入胜的世界。无论是以宏大场景著称的好莱坞商业大片，还是以细腻情感见长的欧洲文艺电影，其背后都有一套成熟而灵活的创作方法论。
      </p>
      <p>
        在表演层面，欧美演员以其扎实的表演功底和对角色的深刻理解，为作品注入了鲜活的生命力。从奥斯卡获奖者的精湛演技到新生代演员的惊艳表现，每一个角色的塑造都是对人性的一次深度探索。
      </p>
      <p>
        技术层面同样值得关注。近年来，欧美影视在视觉特效、音效设计、摄影技术等方面取得了长足进步。IMAX摄影、虚拟制片、杜比全景声等先进技术的广泛应用，为观众带来了前所未有的沉浸式观影体验。
      </p>
      <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-4" id="section-culture">文化视角</h2>
      <p>
        欧美影视不仅是娱乐产品，更是文化传播的重要载体。通过电影和剧集，我们得以窥见不同国家、不同文化背景下的社会风貌、价值观念和人文精神。从美国梦的多元演绎到欧洲传统与现代的碰撞，每一部作品都是一扇了解世界的窗口。
      </p>
      <p>
        在全球化的今天，欧美影视与中国观众的连接也愈发紧密。越来越多的欧美作品在中国市场取得优异成绩，同时中国元素也在欧美影视中频繁出现，形成了双向的文化交流与融合。
      </p>
      <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-4" id="section-conclusion">总结与推荐</h2>
      <p>
        无论您是追求视觉震撼的动作片爱好者，还是偏爱深度思考的文艺片观众，欧美影视的丰富多样性都能满足您的需求。欧美一区二区平台将持续为您提供最新、最全面的影视导航资讯，让您的每一次观影都物超所值。
      </p>
      <p>
        欢迎继续浏览我们的其他分区内容，发现更多精彩的欧美影视作品。我们相信，总有一部作品能够触动您的心弦，成为您心中的经典。
      </p>
    </>
  );
}